import { type ReactNode, createContext, useCallback, useContext, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import { MAX_ATTACHMENT_SIZE_IN_MB, MEGABYTE } from '@/config.ts'
import { type UploadProgress, type UploadType, useFileAttachments } from '@/hooks/useFileAttachments.ts'
import { report } from '@/utils/sentry.ts'

type AttachmentContextType = {
  attachments: Attachment[]
  initializeAttachments: (comments: StoryComment[]) => void
  addAttachment: (files: File[], storyId: string, commentId?: string) => Promise<void>
  getCommentAttachments: (commentId: string) => Attachment[]
  removeAttachment: (attachmentId: string) => void
  downloadAttachment: (attachmentId: string) => Promise<void>
  uploadProgress: Record<string, UploadProgress>
}

export const AttachmentContext = createContext<AttachmentContextType | null>(null)

type AttachmentProviderProps = {
  children: ReactNode
}

export const AttachmentProvider = ({ children }: AttachmentProviderProps) => {
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const { handleFilesUpload, uploadProgress, download } = useFileAttachments()
  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const initializeAttachments = useCallback((comments: StoryComment[]) => {
    const allAttachments = comments.flatMap((comment) => comment.attachments)
    setAttachments((prev) => {
      const merged = [...prev]
      for (const newAtt of allAttachments) {
        const existingIndex = merged.findIndex((att) => {
          return att.id === newAtt.id
        })
        if (existingIndex !== -1) {
          merged[existingIndex] = { ...merged[existingIndex], ...newAtt }
        } else {
          merged.push(newAtt)
        }
      }
      return merged
    })
  }, [])

  const addAttachment = useCallback(
    async (files: File[], storyId: string, commentId?: string) => {
      const uploads: UploadType[] = []
      try {
        const oversizedFiles = files.filter((file) => file.size > MAX_ATTACHMENT_SIZE_IN_MB * MEGABYTE)
        if (oversizedFiles.length > 0) {
          setErrorMessage(
            `Files over ${MAX_ATTACHMENT_SIZE_IN_MB}MB are not supported. Please choose a smaller file. ${oversizedFiles.map((file) => file.name).join(', ')}`,
          )
          setErrorDialogOpen(true)
          return
        }

        const preliminaryAttachments: Attachment[] = files.map((file) => {
          const uploadId = `${Date.now()}-${file.name}`
          uploads.push({ file, uploadId })
          return {
            id: `${Date.now()}-${file.name}`,
            filename: file.name,
            contentType: file.type,
            size: file.size,
            uploadId: uploadId,
            storyId,
            commentId,
          }
        })
        setAttachments((prev) => [...prev, ...preliminaryAttachments])
        const uploadedAttachments = await handleFilesUpload(uploads)
        setAttachments((prev) => {
          const updatedAttachments = [...prev]
          for (const [uploadId, attachment] of uploadedAttachments) {
            const index = updatedAttachments.findIndex((att) => {
              return att.uploadId === uploadId
            })
            if (index !== -1) {
              updatedAttachments[index] = {
                ...attachment,
                id: attachment.id.toString(),
                commentId,
                uploadId,
                storyId,
              }
            }
          }
          return updatedAttachments
        })
      } catch (error) {
        report(error, { operation: 'file_upload' }, { files })
        setAttachments((prev) => prev.filter((att) => !files.some((f) => f.name === att.filename)))
        throw error
      }
    },
    [handleFilesUpload],
  )

  const getCommentAttachments = useCallback(
    (commentId: string) => {
      return attachments.filter((attachment) => attachment.commentId === commentId)
    },
    [attachments],
  )

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((attachment) => attachment.id !== id))
  }, [])

  const value: AttachmentContextType = {
    attachments,
    initializeAttachments,
    addAttachment,
    getCommentAttachments,
    removeAttachment,
    uploadProgress,
    downloadAttachment: download,
  }

  return (
    <AttachmentContext.Provider value={value}>
      {children}
      <Dialog open={errorDialogOpen} onOpenChange={setErrorDialogOpen}>
        <DialogContent className="p-4 gap-6 [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="font-EN-KR-sub-heading-bold text-contents-primary">
              File size too large!
            </DialogTitle>
            <DialogDescription className="font-EN-KR-body1-regular text-contents-primary">
              {errorMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setErrorDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AttachmentContext.Provider>
  )
}

export const useAttachment = () => {
  const context = useContext(AttachmentContext)
  if (!context) throw new Error('useContext must be used within a AttachmentProvider')
  return context
}
